// ThemeContext.js - Gestion globale du thème (Light/Dark Mode)
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';

const ThemeContext = createContext(null);

export const ThemeProvider = ({ children }) => {
  // Initialisation depuis localStorage avec détection préférence système
  const [isDarkMode, setIsDarkMode] = useState(() => {
    try {
      const savedTheme = localStorage.getItem('theme');

      // Si thème sauvegardé, l'utiliser
      if (savedTheme === 'dark') return true;
      if (savedTheme === 'light') return false;

      // Sinon, détecter préférence système
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    } catch (error) {
      console.error('Erreur lors de l\'initialisation du thème:', error);
      return false; // Fallback mode clair
    }
  });

  // Toggle theme avec useCallback pour éviter re-renders
  const toggleTheme = useCallback(() => {
    setIsDarkMode(prev => !prev);
  }, []);

  // Persister dans localStorage et appliquer classe CSS
  useEffect(() => {
    try {
      // Sauvegarder préférence
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');

      // Appliquer classe CSS sur <html>
      const root = document.documentElement;
      if (isDarkMode) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde du thème:', error);
    }
  }, [isDarkMode]);

  // Écouter les changements de préférence système (optionnel)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e) => {
      // Ne changer que si aucune préférence utilisateur explicite
      const savedTheme = localStorage.getItem('theme');
      if (!savedTheme) {
        setIsDarkMode(e.matches);
      }
    };

    // Ajouter listener (syntaxe moderne)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback pour anciens navigateurs
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, []);

  const value = {
    isDarkMode,
    toggleTheme,
    theme: isDarkMode ? 'dark' : 'light', // Valeur string pour facilité d'utilisation
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Hook custom avec validation (pattern AuthContext)
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
